import {
  Machine,
  assign,
  send,
  StateMachine,
  DoneInvokeEvent,
} from 'xstate';
import { RGUser } from '~/api/models/auth';
import { loginRG } from '~/api/routes/auth/user';

import { rgBarService } from './rgBar.service';
import {
  AuthContext,
  AuthEvent,
  AuthSchema,
} from './types';

export const authMachine: StateMachine<
Partial<AuthContext>,
AuthSchema,
AuthEvent
> = Machine(
  {
    id: 'auth',
    initial: 'determineEntrypoint',
    context: {},
    invoke: {
      src: rgBarService,
      id: 'rgBar',
    },
    states: {
      determineEntrypoint: {
        always: [
          { target: 'loggedIn', cond: (ctx) => !!ctx.testMode },
          { target: 'initializing' },
        ],
      },
      initializing: {
        initial: 'awaitingInit',
        states: {
          awaitingInit: {
            type: 'parallel',
            states: {
              rgBar: {
                initial: 'awaiting',
                states: {
                  awaiting: {
                    on: {
                      'RG_BAR.ready': 'ready',
                    },
                  },
                  ready: {
                    type: 'final',
                  },
                },
              },
              facebook: {
                initial: 'awaiting',
                states: {
                  awaiting: {
                    on: {
                      'FB.ready': 'ready',
                    },
                  },
                  ready: {
                    type: 'final',
                  },
                },
              },
            },
            onDone: 'checkingOAuthStatus',
          },
          checkingOAuthStatus: {
            invoke: {
              src: 'loginToRG',
              onDone: [
                {
                  target: '#auth.loggedOutRG',
                  cond: 'authUrlPresent',
                  actions: assign({ authURL: (_, evt: DoneInvokeEvent<RGUser>) => evt.data.authorization_url }),
                },
                // If user is on the enable page, we can skip checking the FB login status since it is not necessary
                { target: '#auth.alreadyEnabled', cond: 'alreadyEnabled' },
                { target: 'checkingFBLoginStatus' },
              ],
            },
          },
          checkingFBLoginStatus: {
            invoke: {
              src: 'getFBLoginStatus',
            },
            on: {
              'FB.authorized': {
                target: 'loggingInRG',
                actions: assign((ctx, evt) => ({
                  authToken: evt.authResponse.accessToken,
                  userId: evt.authResponse.userID,
                })),
              },
              'FB.notAuthorized': '#auth.loggedOutFB',
            },
          },
          loggingInRG: {
            invoke: {
              src: 'loginToRG',
              onDone: [
                { target: '#auth.notEnabled', cond: 'notEnabled' },
                {
                  target: 'needsTos',
                  cond: 'needsTos',
                  actions: [
                    assign({
                      rgUser: (_, evt: DoneInvokeEvent<RGUser>) => evt.data,
                    }),
                    send(
                      (_, evt: DoneInvokeEvent<RGUser>) => ({
                        type: 'RG_BAR.updateSiteUUID',
                        uuid: evt.data.site_uuid,
                      }),
                      {
                        to: 'rgBar',
                      },
                    ),
                  ],
                },
                {
                  target: 'done',
                  actions: [
                    assign({
                      rgUser: (_, evt: DoneInvokeEvent<RGUser>) => evt.data,
                    }),
                    send(
                      (_, evt: DoneInvokeEvent<RGUser>) => ({
                        type: 'RG_BAR.updateSiteUUID',
                        uuid: evt.data.site_uuid,
                      }),
                      {
                        to: 'rgBar',
                      },
                    ),
                  ],
                },
              ],
            },
          },
          needsTos: {
            on: {
              'TOS.accepted': {
                target: 'done',
                actions: assign({ rgUser: ctx => ({ ...ctx.rgUser, uzer_terms: true }) }),
              },
            },
          },
          done: {
            type: 'final',
          },
        },
        onDone: 'loggedIn',
      },
      loggedIn: {
        on: {
          'FB.logout': 'loggedOutFB',
        },
      },
      notEnabled: {
        entry: 'goToEnable',
      },
      alreadyEnabled: {
        type: 'final',
      },
      loggedOutFB: {
        on: {
          'FB.authorized': {
            target: '#auth.initializing.loggingInRG',
            actions: assign((_, evt) => ({
              authToken: evt.authResponse.accessToken,
              userId: evt.authResponse.userID,
            })),
          },
        },
      },
      loggedOutRG: {
        entry: 'goToAuthUrl',
      },
    },
  },
  {
    actions: {
      goToAuthUrl: (ctx) => {
        window.location.href = ctx.authURL;
      },
      goToEnable: () => {
        window.location.pathname = '/enable.html';
      },
    },
    guards: {
      authUrlPresent: (_, evt: DoneInvokeEvent<RGUser>) => !!evt.data.authorization_url,
      alreadyEnabled: (_, evt: DoneInvokeEvent<RGUser>) => evt.data.enabled && window.location.pathname === '/enable.html',
      notEnabled: (_, evt: DoneInvokeEvent<RGUser>) => !evt.data.enabled && window.location.pathname !== '/enable.html',
      onEnable: () => window.location.pathname === '/enable.html',
      needsTos: (_, evt: DoneInvokeEvent<RGUser>) => !evt.data.uzer_terms,
    },
    services: {
      loginToRG: loginRG,
      getFBLoginStatus: () => (cb) => {
        FB.getLoginStatus((response) => {
          if (response.status === 'connected') {
            cb({
              type: 'FB.authorized',
              authResponse: response.authResponse,
            });
          } else {
            cb({ type: 'FB.notAuthorized' });
          }
        }, true);
      },
    },
  },
);
